import { useEffect, useMemo, useState } from 'react';

import {
	get3pSearchConfig,
	getSearchConfig,
	type TSearch3pConfigProps,
	type TSearchConfigAPIResponse,
} from '@atlassian/dynamic-cards-search-client';

import {
	type PrimaryProductKey,
	type ProductKeys,
	products as supportedProducts,
} from '../../common/constants/products';
import { type OutboundAuthConfigsType } from '../../common/types';
import { useLogException } from '../error-boundary';

const EXTRACT_PRODUCT = /ari:cloud:(.*)?::/;
const EXTRACT_CLOUD_ID = /ari:cloud:.*?\/(.*)/;

export type UseFetchConfigProps = {
	aggAbsoluteUrl?: string;
	aggregatorHostName?: string;
	cloudId?: string;
	/**
	 * When passed this would be used as is to invoke the endpoint which returns search-configuration i.e. list of products accessible by the logged in user.
	 * This is useful for products like Trello where the API gateway is not on the host domain, but a completely different URL.
	 *
	 * No need to pass if the UI is being bootstrapped inside Jira, Confluence or any product tied to these sites.
	 */
	configFetchAbsoluteUrl?: string;
	primaryProduct?: PrimaryProductKey;
	thirdPartyAbsoluteUrl?: string;
	userId?: string | null;
};

const getFirstCloudId = (firstPartyResponse: TSearchConfigAPIResponse) => {
	const match = firstPartyResponse.resources[0]?.id.match(EXTRACT_CLOUD_ID);
	const extractedCloudId = match ? match[1] : '';

	return extractedCloudId;
};

const isNounsAvailable = (firstPartyResponse?: TSearchConfigAPIResponse, cloudId?: string) => {
	if (!cloudId || !firstPartyResponse || !firstPartyResponse.siteMetadata) {
		return false;
	}
	return firstPartyResponse.siteMetadata.find((metadata) => metadata.siteId === cloudId)
		?.isNounsAvailable;
};

/**
 * A react hook which handles fetching of all the config necessary to bootstrap 1P + 3P results.
 */
export const useFetchConfig = ({
	cloudId: cloudIdProp,
	configFetchAbsoluteUrl,
	aggregatorHostName,
	userId,
	thirdPartyAbsoluteUrl,
	primaryProduct,
}: UseFetchConfigProps): {
	accessibleProducts: string[] | undefined;
	firstCloudId: string;
	intercomHmac: string | undefined;
	isNounsAvailable: boolean | undefined;
	outboundAuthConfigs:
		| Partial<
				Record<
					| 'confluence'
					| 'townsquare'
					| 'jira'
					| 'asana'
					| 'bitbucket'
					| 'compass'
					| 'drive'
					| 'jira-product-discovery'
					| 'jira-servicedesk'
					| 'opsgenie'
					| 'sharepoint'
					| 'slack'
					| 'trello'
					| 'atlassian-web-crawler'
					| 'teams'
					| 'figma'
					| 'github'
					| 'loom'
					| 'onedrive'
					| 'google-calendar'
					| 'dropbox'
					| 'gmail'
					| 'outlook-calendar'
					| 'airtable'
					| 'zendesk'
					| 'notion'
					| 'miro',
					{
						url: string;
						userNeedsOAuth: boolean;
					}
				>
		  >
		| Record<string, never>;
} => {
	const [firstPartyResponse, setFirstPartyResponse] = useState<TSearchConfigAPIResponse>();
	const [accessibleProducts, setAccessibleProducts] = useState<string[]>();

	const logException = useLogException();

	const firstCloudId = useMemo(() => {
		if (!cloudIdProp && firstPartyResponse) {
			return getFirstCloudId(firstPartyResponse);
		}
		return '';
	}, [firstPartyResponse, cloudIdProp]);

	const { productKeys: thirdPartyProductKeys, outboundAuthConfigs } = useThirdPartyConfig({
		cloudId: cloudIdProp || firstCloudId || '',
		userId: userId,
		absoluteUrl: thirdPartyAbsoluteUrl,
	});

	useEffect(() => {
		if (!firstPartyResponse) {
			getSearchConfig({
				cloudId: cloudIdProp,
				absoluteUrl: configFetchAbsoluteUrl,
				hostName: aggregatorHostName,
				userId,
			})
				.then((response) => setFirstPartyResponse(response))
				.catch((error) => {
					logException(error);

					setFirstPartyResponse({
						resources: [],
						intercomHmac: '',
						siteMetadata: [],
					});
				});
		}
	}, [
		firstPartyResponse,
		setFirstPartyResponse,
		cloudIdProp,
		configFetchAbsoluteUrl,
		aggregatorHostName,
		userId,
		logException,
	]);

	useEffect(() => {
		if (thirdPartyProductKeys && firstPartyResponse && !accessibleProducts) {
			let allAccessibleProducts: string[] = [];

			const firstPartyProducts = firstPartyResponse.resources.flatMap((resource) => {
				const { id } = resource;
				const match = id.match(EXTRACT_PRODUCT);

				if (match && match.length >= 2) {
					return [match[1]];
				}

				return [];
			});

			if (firstPartyProducts.length === 0 && primaryProduct) {
				// Use the primary product if the 1P Search Config request fails
				allAccessibleProducts.push(primaryProduct);
			} else {
				allAccessibleProducts.push(...firstPartyProducts);
			}

			// Remove Bitbucket from the list of resources if Jira is not available.
			// This is because Bitbucket results only come from workspaces that are connected to a corresponding Jira site
			// See https://hello.atlassian.net/wiki/x/1hI6CQE for details
			if (allAccessibleProducts.includes('bitbucket') && !allAccessibleProducts.includes('jira')) {
				allAccessibleProducts = allAccessibleProducts.filter((product) => product !== 'bitbucket');
			}

			const allProducts = allAccessibleProducts.concat(thirdPartyProductKeys);

			// This is the source of truth for availability of loom integration
			// After GA this will become a TCS query
			// fg('confluence_loom_beta_enabled') => false
			if (false) {
				allProducts.push('loom');
			}

			setAccessibleProducts(allProducts);
		}
	}, [
		thirdPartyProductKeys,
		firstPartyResponse,
		setAccessibleProducts,
		primaryProduct,
		accessibleProducts,
		logException,
	]);

	return {
		accessibleProducts,
		outboundAuthConfigs,
		firstCloudId,
		intercomHmac: firstPartyResponse?.intercomHmac,
		isNounsAvailable: isNounsAvailable(firstPartyResponse, cloudIdProp),
	};
};

const THIRD_PARTY_TYPES: Record<string, string> = {
	'ai-3p-connector:google-workspace': `drive`,
	'ai-3p-connector:sharepoint-domain': `sharepoint`,
	'ai-3p-connector:slack-workspace': 'slack',
	//platform connector types use a different naming convention
	'sharepoint-connector': `sharepoint`,
	'webcrawler-connector': 'atlassian-web-crawler',
	'microsoft-teams-connector': 'teams',
	'github-connector': 'github',
	'figma-connector': 'figma',
	'onedrive-connector': 'onedrive',
	'dropbox-connector': 'dropbox',
	'google-calendar-connector': 'google-calendar',
	'gmail-conenctor': 'gmail',
	'airtable-connector': 'airtable',
	'notion-connector': 'notion',
	'miro-connector': 'miro',
} as const;

const get3pProductKeys = async ({ cloudId, absoluteUrl, userId }: TSearch3pConfigProps) => {
	const resources = await get3pSearchConfig({ cloudId, absoluteUrl, userId });

	let productKeys = [] as ProductKeys[];
	let outboundAuthConfigs = {} as OutboundAuthConfigsType;

	resources.forEach((resource) => {
		const productKey = THIRD_PARTY_TYPES[resource.type] || '';
		if (productKey) {
			productKeys.push(productKey as ProductKeys);
			outboundAuthConfigs[productKey as ProductKeys] = {
				url: resource.outboundAuthUrl,
				userNeedsOAuth: !resource.isUserOAuthed,
			};
		}
	});

	productKeys = productKeys.filter((productKey) => supportedProducts[productKey]);

	return { productKeys, outboundAuthConfigs };
};

const EMPTY_RESPONSE: string[] = [];

export const useThirdPartyConfig = ({
	cloudId,
	absoluteUrl,
	userId,
}: Partial<TSearch3pConfigProps> = {}): {
	outboundAuthConfigs:
		| Partial<
				Record<
					| 'confluence'
					| 'townsquare'
					| 'jira'
					| 'asana'
					| 'bitbucket'
					| 'compass'
					| 'drive'
					| 'jira-product-discovery'
					| 'jira-servicedesk'
					| 'opsgenie'
					| 'sharepoint'
					| 'slack'
					| 'trello'
					| 'atlassian-web-crawler'
					| 'teams'
					| 'figma'
					| 'github'
					| 'loom'
					| 'onedrive'
					| 'google-calendar'
					| 'dropbox'
					| 'gmail'
					| 'outlook-calendar'
					| 'airtable'
					| 'zendesk'
					| 'notion'
					| 'miro',
					{
						url: string;
						userNeedsOAuth: boolean;
					}
				>
		  >
		| Record<string, never>;
	productKeys: string[] | undefined;
} => {
	const [productKeys, setProductKeys] = useState<string[] | undefined>(undefined);
	const [outboundAuthConfigs, setOutboundAutConfigs] = useState<OutboundAuthConfigsType>({});
	const logException = useLogException();

	useEffect(() => {
		if (!productKeys) {
			if (cloudId) {
				get3pProductKeys({ cloudId, absoluteUrl, userId })
					.then((data) => {
						setProductKeys(data.productKeys);

						if (data.outboundAuthConfigs) {
							setOutboundAutConfigs(data.outboundAuthConfigs);
						}
					})
					.catch((error) => {
						setProductKeys(EMPTY_RESPONSE);
						logException(error);
					});
			} else {
				setProductKeys(EMPTY_RESPONSE);
			}
		}
	}, [cloudId, absoluteUrl, userId, productKeys, logException]);

	return { productKeys, outboundAuthConfigs };
};
